import { Config } from 'config/envConfig';
import {
    faCircleXmark,
    faExclamationTriangle,
    faFileCirclePlus,
    faFileImport,
    faShuffle,
} from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import {
    alert,
    AlertType,
    Button,
    ConfirmModal,
    DragDropFile,
    FileListViewer,
    LoadingView,
    Modal,
} from '@quyva/shared-ui';
import { isMobile } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFileNameWithoutExt } from '@quyva/common';
import { getUrl } from '../../utils';
import axios from 'axios';

export const ImportDashboardView = ({
    user,
    setViewImportModal,
    getDashboardList,
    setSelectedDashboard,
    dashboardList,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [dashboardExist, setDashboardExist] = useState(false);
    const [viewConfirmModal, setViewConfirmModal] = useState(false);

    const fileInputRef = useRef(null);

    useEffect(() => {
        if (selectedFile) {
            validateFile(selectedFile);
        }
    }, [selectedFile]);

    const checkIfDashboardExists = (searchString, dashboardList) => {
        let exist = false;

        if (dashboardList && dashboardList.length) {
            for (let i = 0; i < dashboardList.length; i++) {
                const fileName = dashboardList[i].name;

                if (fileName === getFileNameWithoutExt(searchString)) {
                    // there exists a file with same name
                    exist = true;
                    break;
                }
            }
        }

        // no matching file present
        return exist;
    };

    // Validate file types
    const validateFile = (file) => {
        setError('');

        if (!file) {
            setError('Please select a file to upload.');
            return;
        }

        // Ensure only JSON files are uploaded
        if (file.type !== 'application/json') {
            setError('Please upload a valid JSON file.');
            return;
        }

        // Reading File
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target.result);
                const name = jsonData.dashboardName || null;
                if (name) {
                    if (checkIfDashboardExists(name, dashboardList)) {
                        setDashboardExist(true);
                    }
                } else {
                    setError('Invalid file format : Dashboard name missing.');
                }
            } catch (error) {
                setError('Invalid JSON format.');
            }
        };

        reader.onerror = () => {
            setError(`Error reading ${file.name} file.`);
        };

        reader.readAsText(file);
    };

    const importDashboard = async (file, createCopy = false) => {
        try {
            setLoadingText('Importing Dashboard...');
            setIsLoading(false);

            const { email } = user;

            const formData = new FormData();
            formData.append('dashboardFile', file);
            formData.append('userId', email);
            formData.append('createCopy', createCopy);

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/dashboard/import`
            );

            const authToken = encodeURIComponent(user.token);

            const response = await axios.post(dynamicUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: authToken,
                },
            });

            if (response.status === 200) {
                const { message, dashboardName } = response.data;
                alert(
                    AlertType.SUCCESS,
                    `${dashboardName} imported successfully`
                );
                const list = await getDashboardList();

                const data = list.find(
                    (dashboard) => dashboard.name === dashboardName
                );

                if (data) {
                    setSelectedDashboard(data);
                }

                setSelectedFile(null);
                setViewImportModal(false);
            } else {
                alert(AlertType.ERROR, 'Dashboard import failed');
            }
        } catch (error) {
            alert(
                AlertType.ERROR,
                error.response?.data?.error || error.message
            );
        } finally {
            setLoadingText('');
            setIsLoading(false);
        }
    };

    // Handle file selection
    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFile(files[0]);
    };

    // Handle file drop
    const handleFileDrop = (event) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        setSelectedFile(files[0]);

        // Programmatically set the files of the file input
        if (fileInputRef.current) {
            const dataTransfer = new DataTransfer();
            files.forEach((file) => dataTransfer.items.add(file));
            fileInputRef.current.files = dataTransfer.files;
        }
    };

    // Prevent default behavior for file drop
    const handleDragOver = (event) => {
        event.preventDefault();
    };

    // Handle form submission
    const handleImport = async () => {
        if (dashboardExist) {
            setViewConfirmModal(true);
        } else {
            await importDashboard(selectedFile, false);
            setViewImportModal(false);
        }
    };

    return (
        <Modal
            title={'Import Dashboard'}
            titleIcon={faFileImport}
            iconColor={'sky'}
            onClose={() => setViewImportModal(false)}
            className={''}
        >
            <div className='w-[60vw] min-w-[500px] flex-col px-6 py-6 rounded-xl bg-backgrounds-slate text-texts-secondary300'>
                {isLoading ? (
                    <div className='w-full h-32 flex justify-center items-center w-full h-full py-6 my-1.5'>
                        <LoadingView loadingText={loadingText} inLine={false} />
                    </div>
                ) : (
                    <div>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='.json'
                            className='bg-backgrounds-white w-full p-2 rounded-xl border cursor-pointer'
                            onChange={handleFileSelect}
                            single
                            style={{
                                maxWidth: '80rem', // or a specific width like '20rem' based on your layout
                                textOverflow: 'ellipsis', // adds ellipsis to overflowing text
                                overflow: 'hidden', // hides overflowing text
                            }}
                        />

                        {!isMobile && (
                            <DragDropFile
                                onDrop={(e) => {
                                    const files = e.dataTransfer.files;
                                    if (files.length > 0) {
                                        handleFileDrop(e); // Proceed with the file drop
                                    }
                                }}
                                onDragOver={handleDragOver}
                                text={
                                    'Drag and drop file here or click on choose file icon to Import file'
                                }
                            />
                        )}

                        <FileListViewer
                            selectedFiles={selectedFile ? [selectedFile] : []}
                        />

                        <div className='min-h-[20px] flex justify-center items-center text-buttons-alert400 mt-3'>
                            {error && (
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className='mr-2'
                                />
                            )}
                            {error}
                        </div>

                        <div className='flex items-center justify-center mt-4'>
                            <div className='flex items-center justify-between w-full'>
                                <Button
                                    type='danger'
                                    icon={faCircleXmark}
                                    className='w-1/3'
                                    text={'Cancel'}
                                    hoverText={'Cancel and close the modal'}
                                    onClick={() => {
                                        setViewImportModal(false);
                                    }}
                                />
                                <Button
                                    type='success'
                                    icon={faFileImport}
                                    className='w-1/3'
                                    text={'Import'}
                                    hoverText={'Import Dashboard'}
                                    onClick={async () => {
                                        await handleImport();
                                    }}
                                    disabled={error || !selectedFile}
                                />
                            </div>
                        </div>

                        {viewConfirmModal && (
                            <ConfirmModal
                                modalText={`A dashboard with name '${getFileNameWithoutExt(
                                    selectedFile.name
                                )}' already exists. Do you want replace the existing dashboard or create a new one from your file?`}
                                confirmText={'New'}
                                confirmIcon={faFileCirclePlus}
                                cancelText={'Replace'}
                                cancelIcon={faShuffle}
                                onConfirm={async () => {
                                    await importDashboard(selectedFile, true);
                                    setViewConfirmModal(false);
                                    setViewImportModal(false);
                                }}
                                onCancel={async () => {
                                    await importDashboard(selectedFile, false);
                                    setViewConfirmModal(false);
                                    setViewImportModal(false);
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
